import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Settings2, Save, RotateCcw, Loader2 } from 'lucide-react';
import { analysesApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const gradeColors = {
  A: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', ring: 'ring-emerald-500/30' },
  B: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400', ring: 'ring-indigo-500/30' },
  C: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', ring: 'ring-amber-500/30' },
  D: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', ring: 'ring-orange-500/30' },
  F: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', ring: 'ring-red-500/30' },
};

const DEFAULT_WEIGHTS = {
  dscr_weight: 25,
  valuation_weight: 25,
  margins_weight: 15,
  solvency_weight: 15,
  trends_weight: 10,
  risk_weight: 10,
};

const WEIGHT_LABELS = {
  dscr_weight: 'DSCR (Debt Service)',
  valuation_weight: 'Valuation (EV/Asking)',
  margins_weight: 'Margins (GM/OM)',
  solvency_weight: 'Solvency (CR/D/E)',
  trends_weight: 'Trends (Revenue)',
  risk_weight: 'Risk Factors',
};

export function DealScoreRing({ score, grade, size = 'lg' }) {
  const colors = gradeColors[grade] || gradeColors.C;
  const sizePx = size === 'lg' ? 120 : size === 'md' ? 80 : 56;
  const r = size === 'lg' ? 40 : size === 'md' ? 28 : 20;
  const stroke = size === 'lg' ? 6 : 4;
  const circ = 2 * Math.PI * r;
  const prog = (score / 100) * circ;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: sizePx, height: sizePx }}>
      <svg className="transform -rotate-90" width={sizePx} height={sizePx}>
        <circle cx={sizePx / 2} cy={sizePx / 2} r={r} stroke="#27272a" strokeWidth={stroke} fill="none" />
        <circle cx={sizePx / 2} cy={sizePx / 2} r={r} stroke="currentColor"
          strokeWidth={stroke} fill="none"
          strokeDasharray={circ} strokeDashoffset={circ - prog}
          strokeLinecap="round"
          className={`${colors.text} transition-all duration-1000 ease-out`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-mono font-bold ${colors.text} ${size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-sm'}`}>{score}</span>
        {size !== 'sm' && <span className={`text-[10px] ${colors.text} font-medium`}>{grade}</span>}
      </div>
    </div>
  );
}

export function WeightConfigPanel({ analysisId, currentWeights, onUpdate }) {
  const [weights, setWeights] = useState(() => ({
    ...DEFAULT_WEIGHTS,
    ...currentWeights,
  }));
  const [saving, setSaving] = useState(false);

  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const isValid = total === 100;

  const handleSliderChange = useCallback((key, value) => {
    setWeights(prev => ({ ...prev, [key]: value[0] }));
  }, []);

  const handleReset = useCallback(() => {
    setWeights({ ...DEFAULT_WEIGHTS });
  }, []);

  const handleSave = useCallback(async () => {
    if (!isValid || !analysisId) return;
    setSaving(true);
    try {
      const res = await analysesApi.updateDealScoreWeights(analysisId, weights);
      toast.success('Weights updated & score recalculated');
      if (onUpdate) onUpdate(res.data.deal_score);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update weights');
    } finally {
      setSaving(false);
    }
  }, [analysisId, weights, isValid, onUpdate]);

  return (
    <div className="space-y-4 p-4 bg-zinc-800/40 rounded-lg border border-zinc-700/50">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium text-zinc-300 flex items-center gap-2">
          <Settings2 className="w-3.5 h-3.5 text-indigo-400" />
          Configure Weights
        </h4>
        <Badge variant="outline" className={`text-[10px] ${isValid ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
          Total: {total}/100
        </Badge>
      </div>

      <div className="space-y-3">
        {Object.entries(WEIGHT_LABELS).map(([key, label]) => (
          <div key={key} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-400">{label}</span>
              <span className="text-[10px] font-mono text-zinc-300 w-8 text-right">{weights[key]}</span>
            </div>
            <Slider
              value={[weights[key]]}
              onValueChange={(v) => handleSliderChange(key, v)}
              max={50}
              min={0}
              step={5}
              className="w-full"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-zinc-700/50">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="flex-1 border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs"
        >
          <RotateCcw className="w-3 h-3 mr-1" /> Reset
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!isValid || saving}
          className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white text-xs"
        >
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Apply
        </Button>
      </div>

      {!isValid && (
        <p className="text-[10px] text-red-400 text-center">
          Weights must sum to exactly 100
        </p>
      )}
    </div>
  );
}

export function DealScoreCard({ dealScore, analysisId, onScoreUpdate, compact = false, showConfig = false }) {
  const [configOpen, setConfigOpen] = useState(false);
  
  if (!dealScore) return null;
  const { score, grade, assessment, breakdown, weights } = dealScore;
  const colors = gradeColors[grade] || gradeColors.C;

  if (compact) {
    return (
      <div className={`p-3 rounded-lg border ${colors.border} ${colors.bg} flex items-center gap-3`}>
        <DealScoreRing score={score} grade={grade} size="sm" />
        <div>
          <div className={`text-xs font-semibold ${colors.text}`}>{assessment}</div>
          <div className="text-[10px] text-zinc-500">Deal Score: {score}/100</div>
        </div>
      </div>
    );
  }

  return (
    <Card className={`bg-zinc-900/60 ${colors.border}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Deal Score</CardTitle>
          <div className="flex items-center gap-2">
            {showConfig && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfigOpen(!configOpen)}
                className={`h-7 px-2 ${configOpen ? 'bg-indigo-500/20 text-indigo-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                data-testid="configure-weights-btn"
              >
                <Settings2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <Badge variant="outline" className={`${colors.border} ${colors.text} text-xs`}>{assessment}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-6">
          <DealScoreRing score={score} grade={grade} size="lg" />
          <div className="flex-1 space-y-1.5">
            {Object.entries(breakdown || {}).map(([key, b]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] text-zinc-400">{b.label}</span>
                    <span className="text-[10px] font-mono text-zinc-300">{b.points}/{b.max}</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-700 ${colors.text.replace('text-', 'bg-')}`}
                      style={{ width: `${(b.points / b.max) * 100}%` }}
                    />
                  </div>
                </div>
                {b.value && <span className="text-[10px] font-mono text-zinc-500 w-20 text-right shrink-0">{b.value}</span>}
              </div>
            ))}
          </div>
        </div>

        {configOpen && (
          <WeightConfigPanel
            analysisId={analysisId}
            currentWeights={weights}
            onUpdate={onScoreUpdate}
          />
        )}
      </CardContent>
    </Card>
  );
}
